// hooks/usePlanningData.js

import { useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AuthContext from '../../../contexts/authContext';
import CompanieContext from '../../../contexts/companieContext';


import {
	CHRONOLOGIE_TYPES,
	INTERVENTION_TYPES,
	INTERVENTION_MOTIFS,
	INTERVENTION_STATUTS,
	CAR_STATUTS,
	loadClients,
	loadTechniciens,
	extractTechniciensFromCars,
	loadCars,
	loadInterventions,
	getInterventionById as serviceGetInterventionById,
	getInterventionByIdMobile as serviceGetInterventionByIdMobile,
	addIntervention as serviceAddIntervention,
	updateIntervention as serviceUpdateIntervention,
	deleteIntervention as serviceDeleteIntervention,
	transferInterventions as serviceTransferInterventions,
	loadChronologieByIntervention as serviceLoadChronologieByIntervention,
	addChronologieEntry as serviceAddChronologieEntry,
	loadRapports as serviceLoadRapports,
	createRapport as serviceCreateRapport,
	updateRapport as serviceUpdateRapport,
	deleteRapport as serviceDeleteRapport,
	getRapportByIntervention as serviceGetRapportByIntervention,
	addPaiement as serviceAddPaiement,
	updatePaiement as serviceUpdatePaiement,
	deletePaiement as serviceDeletePaiement,
	getPaiementsByRapport as serviceGetPaiementsByRapport,
	getPaiementStats as serviceGetPaiementStats,
	searchPaiements as serviceSearchPaiements,
	createGlobalPaiement as serviceCreateGlobalPaiement,
	updateGlobalPaiement as serviceUpdateGlobalPaiement,
	syncPaiements as serviceSyncPaiements,
	getGlobalPaiementDetails as serviceGetGlobalPaiementDetails,
} from './planningService';

// =========== CONSTANTS ===========
const STATUS_COLORS = {
	planifie: { background: '#e3f2fd', text: '#1976d2' },
	en_cours: { background: '#fff3e0', text: '#f57c00' },
	termine: { background: '#e8f5e8', text: '#388e3c' },
	annule: { background: '#ffebee', text: '#d32f2f' },
	default: { background: '#f5f5f5', text: '#757575' },
};

const DEFAULT_FILTERS = {
	date_debut: (() => {
		const date = new Date();
		date.setHours(0, 0, 0, 0);
		return date;
	})(),
	date_fin: (() => {
		const date = new Date();
		date.setHours(23, 59, 59, 999);
		date.setDate(date.getDate() + 6); // Add 6 days for week view
		return date;
	})(),
	viewMode: 'week', // Default to week view
	carFilter: 'all',
	technicienFilter: 'all',
	typeFilter: 'all',
	statutFilter: 'all',
	reference: '',
};

const DEBOUNCE_DELAY = 300;

// =========== HELPER FUNCTIONS ===========
const createWeekFromDate = (date) => {
	const today = new Date(date);
	const first = today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1);
	const monday = new Date(today.setDate(first));

	return Array.from({ length: 7 }, (_, i) => {
		const date = new Date(monday);
		date.setDate(monday.getDate() + i);
		return date;
	});
};

const getWeekDateRange = (startDate = new Date()) => {
	const start = new Date(startDate);
	start.setHours(0, 0, 0, 0);

	// Calculate end date (start date + 6 days)
	const end = new Date(start);
	end.setDate(start.getDate() + 6);
	end.setHours(23, 59, 59, 999);

	return {
		date_debut: start,
		date_fin: end,
	};
};

// =========== CUSTOM HOOK ===========
export const usePlanningData = () => {
	// =========== CONTEXTS ===========
	const { userData } = useContext(AuthContext);
	const { companies } = useContext(CompanieContext);

	// =========== REFS ===========
	const companiesRef = useRef(companies);
	const userDataRef = useRef(userData);
	const isInitialMount = useRef(true);

	// =========== STATE ===========
	const [cars, setCars] = useState([]);
	const [interventions, setInterventions] = useState([]);
	const [clients, setClients] = useState([]);
	const [techniciens, setTechniciens] = useState([]);
	const [chronologies, setChronologies] = useState([]);
	const [rapports, setRapports] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [filters, setFilters] = useState(DEFAULT_FILTERS);

	// =========== REFS UPDATES ===========
	useEffect(() => {
		companiesRef.current = companies;
		userDataRef.current = userData;
	}, [companies, userData]);

	// =========== CORE DATA OPERATIONS ===========
	const updateInterventionState = useCallback((interventionId, updatedData) => {
		setInterventions((prev) =>
			prev.map((intervention) =>
				intervention._id === interventionId ? updatedData : intervention,
			),
		);
	}, []);

	// =========== DATA LOADING FUNCTIONS ===========
	const loadAllData = useCallback(
		async (filtersToLoad = {}) => {
			setLoading(true);
			setError(null);

			try {
				const loadAllCars = async () => {
					const carFilters = {
						carFilter: 'all',
						technicienFilter: 'all',
						statutFilter: 'all',
					};
					return await loadCars(carFilters, companiesRef.current);
				};
				const loadCarsWithFilters = async () => {
					const carFilters = {
						carFilter: filtersToLoad.carFilter || filters.carFilter,
						technicienFilter:
							filtersToLoad.technicienFilter || filters.technicienFilter,
						statutFilter: filtersToLoad.statutFilter || filters.statutFilter,
					};
					return await loadCars(carFilters, companiesRef.current);
				};
				const loadInterventionsWithFilters = async () => {
					// Always use week view
					const currentViewMode = 'week'; // Force week view
					const baseDate = filtersToLoad.date_debut || filters.date_debut || new Date();

					// Calculate week range from the base date
					const weekRange = getWeekDateRange(baseDate);

					const interventionFilters = {
						reference: filtersToLoad.reference || filters.reference,
						typeFilter: filtersToLoad.typeFilter || filters.typeFilter,
						statutFilter: filtersToLoad.statutFilter || filters.statutFilter,
						date_debut: weekRange.date_debut,
						date_fin: weekRange.date_fin,
						viewMode: currentViewMode,
					};

					console.log('📅 Loading interventions for week:', {
						start: interventionFilters.date_debut,
						end: interventionFilters.date_fin,
						viewMode: interventionFilters.viewMode,
					});

					return await loadInterventions(interventionFilters);
				};
				const [clientsData, techniciensData, carsData, interventionsData, rapportsData] =
					await Promise.all([
						loadClients(),
						loadTechniciens(),
						loadCarsWithFilters(),
						loadInterventionsWithFilters(),
						//serviceLoadRapports(),
					]);

				setClients(clientsData);
				setTechniciens(techniciensData);
				setCars(carsData);
				setInterventions(interventionsData);
				//setRapports(rapportsData);

				// Extract techs from cars if no techniciens loaded
				if (techniciensData.length === 0 && carsData.length > 0) {
					const techs = extractTechniciensFromCars(carsData);
					if (techs.length > 0) {
						setTechniciens(techs);
					}
				}

				return { clientsData, techniciensData, carsData, interventionsData, rapportsData };
			} catch (err) {
				setError('Erreur lors du chargement des données');
				console.error('Load data error:', err);
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[filters],
	);

	
	const getWeekDateRange = (date = new Date()) => {
		const currentDate = new Date(date);
		const dayOfWeek = currentDate.getDay(); 

		const startOfWeek = new Date(currentDate);
		const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
		startOfWeek.setDate(diff);
		startOfWeek.setHours(0, 0, 0, 0);

		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(startOfWeek.getDate() + 6);
		endOfWeek.setHours(23, 59, 59, 999);

		return {
			date_debut: startOfWeek,
			date_fin: endOfWeek,
		};
	};

	const loadInitialData = useCallback(async () => {
		await loadAllData();
	}, [loadAllData]);

	const loadClientsData = useCallback(async () => {
		try {
			const data = await loadClients();
			setClients(data);
			return data;
		} catch (err) {
			console.error('Load clients error:', err);
			throw err;
		}
	}, []);

	const loadTechniciensData = useCallback(async () => {
		try {
			const data = await loadTechniciens();
			setTechniciens(data);
			return data;
		} catch (err) {
			console.error('Load techniciens error:', err);
			throw err;
		}
	}, []);

	const loadCarsData = useCallback(
		async (currentFilters = {}) => {
			try {
				const filterParams = {
					carFilter: currentFilters.carFilter || filters.carFilter,
					technicienFilter: currentFilters.technicienFilter || filters.technicienFilter,
					statutFilter: currentFilters.statutFilter || filters.statutFilter,
				};

				const data = await loadCars(filterParams, companiesRef.current);
				setCars(data);
				return data;
			} catch (err) {
				console.error('Load cars error:', err);
				throw err;
			}
		},
		[filters.carFilter, filters.technicienFilter, filters.statutFilter],
	);

	const loadInterventionsData = useCallback(
		async (currentFilters = {}) => {
			setLoading(true);
			try {
				// Get date range based on view mode
				let dateRange;

				/* // Check if a specific technician is selected (not "all")
    const hasTechnicianFilter = currentFilters.technicienFilter && 
                               currentFilters.technicienFilter !== 'all'; */

				// If technician is selected OR viewMode is week, use week view
				const effectiveViewMode = 'week';

				if (effectiveViewMode === 'week') {
					// Calculate week range
					const baseDate = currentFilters.date_debut || filters.date_debut || new Date();
					const weekRange = getWeekDateRange(baseDate);
					dateRange = weekRange;
				} else {
					// Day view
					const baseDate = currentFilters.date_debut || filters.date_debut || new Date();
					const dayStart = new Date(baseDate);
					dayStart.setHours(0, 0, 0, 0);
					const dayEnd = new Date(baseDate);
					dayEnd.setHours(23, 59, 59, 999);
					dateRange = { date_debut: dayStart, date_fin: dayEnd };
				}

				const filterParams = {
					reference: currentFilters.reference || filters.reference,
					typeFilter: currentFilters.typeFilter || filters.typeFilter,
					statutFilter: currentFilters.statutFilter || filters.statutFilter,
					date_debut: dateRange.date_debut,
					date_fin: dateRange.date_fin,
					viewMode: effectiveViewMode,
					/*  technicienFilter: currentFilters.technicienFilter || filters.technicienFilter */
				};

				console.log('🔍 Loading interventions with params:', {
					date_debut: filterParams.date_debut,
					date_fin: filterParams.date_fin,
					viewMode: filterParams.viewMode,
					/* technician: filterParams.technicienFilter */
				});

				const data = await loadInterventions(filterParams);
				setInterventions(data);
				return data;
			} catch (err) {
				console.error('Load interventions error:', err);
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[
			filters.reference,
			filters.typeFilter,
			filters.statutFilter,
			filters.date_debut,
			filters.date_fin,
			filters.viewMode,
			filters.technicienFilter,
		],
	);
	// =========== INTERVENTION OPERATIONS ===========
	const getInterventionById = useCallback(async (interventionId) => {
		if (!interventionId) return null;
		return await serviceGetInterventionById(interventionId);
	}, []);

	const getInterventionByIdMobile = useCallback(async (interventionId) => {
		if (!interventionId) return null;
		return await serviceGetInterventionByIdMobile(interventionId);
	}, []);

	const refreshIntervention = useCallback(
		async (interventionId) => {
			if (!interventionId) return null;
			const refreshed = await serviceGetInterventionById(interventionId);
			if (refreshed) updateInterventionState(interventionId, refreshed);
			return refreshed;
		},
		[updateInterventionState],
	);

	const addIntervention = useCallback(async (interventionData) => {
		const data = await serviceAddIntervention(interventionData);
		if (data) setInterventions((prev) => [...prev, data]);
		return data;
	}, []);

	const updateIntervention = useCallback(
		async (interventionId, updates) => {
			const updatesWithUser = {
				...updates,
				updatedBy: userDataRef.current?._id,
			};

			const result = await serviceUpdateIntervention(interventionId, updatesWithUser);

			if (result) {
				// 1. Update local state immediately
				updateInterventionState(interventionId, result);

				// 2. Reload cars data (car status might change)
				loadInterventions()

				// 3. Return the result
				return result;
			}

			return result;
		},
		[updateInterventionState, loadCarsData],
	);

	const deleteIntervention = useCallback(async (interventionId) => {
		const success = await serviceDeleteIntervention(interventionId);
		if (success) setInterventions((prev) => prev.filter((i) => i._id !== interventionId));
		return success;
	}, []);

	const transferInterventions = useCallback(
		async (interventionIds, targetCarId, fromCarId) => {
			const success = await serviceTransferInterventions(
				interventionIds,
				targetCarId,
				fromCarId,
			);

			if (success) {
				const ids = Array.isArray(interventionIds) ? interventionIds : [interventionIds];
				const refreshPromises = ids.map((id) => refreshIntervention(id));
				await Promise.all(refreshPromises);

				// Refresh cars data
				await loadCarsData();
			}

			return success;
		},
		[refreshIntervention, loadCarsData],
	);

	// =========== CHRONOLOGIE OPERATIONS ===========
	const addChronologieEntry = useCallback(async (interventionId, entryData) => {
		return await serviceAddChronologieEntry(interventionId, entryData, userDataRef.current?.id);
	}, []);

	const getChronologieByIntervention = useCallback(async (interventionId) => {
		if (!interventionId) {
			return {
				_id: `chrono-default-${interventionId}`,
				intervention: interventionId,
				entries: [],
			};
		}
		return await serviceLoadChronologieByIntervention(interventionId);
	}, []);

	const loadChronologieByIntervention = useCallback(async (interventionId) => {
		const result = await serviceLoadChronologieByIntervention(interventionId);

		if (result) {
			setChronologies((prev) => {
				const existingIndex = prev.findIndex((c) => c.intervention === interventionId);
				if (existingIndex >= 0) {
					const updated = [...prev];
					updated[existingIndex] = result;
					return updated;
				}
				return [...prev, result];
			});
		}

		return result;
	}, []);

	// =========== RAPPORT OPERATIONS ===========
	const createRapport = useCallback(async (rapportData) => {
		const result = await serviceCreateRapport(rapportData);
		if (result && result.success) {
			setRapports((prev) => [...prev, result.data]); // Store only the data
			console.log('result from useplanning', result.data);
			return result.data; // Return only the data
		}
		console.log('result from useplanning', result);
		return result;
	}, []);

	const updateRapport = useCallback(
		async (rapportId, updates) => {
			const result = await serviceUpdateRapport(rapportId, updates);

			if (result) {
				setRapports((prev) => prev.map((r) => (r._id === rapportId ? result : r)));
				if (result.intervention) {
					await refreshIntervention(result.intervention?._id);
				}
			}

			return result;
		},
		[refreshIntervention],
	);

	const deleteRapport = useCallback(async (rapportId) => {
		const success = await serviceDeleteRapport(rapportId);
		if (success) setRapports((prev) => prev.filter((r) => r._id !== rapportId));
		return success;
	}, []);

	const getRapportByIntervention = useCallback(async (interventionId) => {
		if (!interventionId) return null;
		return await serviceGetRapportByIntervention(interventionId);
	}, []);

	const loadRapportByIntervention = useCallback(async (interventionId) => {
		const result = await serviceGetRapportByIntervention(interventionId);

		if (result) {
			setRapports((prev) => {
				const existingIndex = prev.findIndex((r) => r.intervention === interventionId);
				if (existingIndex >= 0) {
					const updated = [...prev];
					updated[existingIndex] = result;
					return updated;
				}
				return [...prev, result];
			});
		}

		return result;
	}, []);

	// =========== UTILITY FUNCTIONS ===========
	const getInterventionsByCarAndDate = useCallback(
		(carId, date) => {
			if (!carId) return [];

			let filtered = interventions.filter((intervention) => {
				if (!intervention.date_debut) return false;
				const interventionDate = new Date(intervention.date_debut).toDateString();
				const targetDate = date.toDateString();
				return intervention.car === carId && interventionDate === targetDate;
			});

			if (filters.typeFilter !== 'all') {
				filtered = filtered.filter((i) => i.type === filters.typeFilter);
			}

			if (filters.statutFilter !== 'all') {
				filtered = filtered.filter((i) => i.statut === filters.statutFilter);
			}

			return filtered;
		},
		[interventions, filters.typeFilter, filters.statutFilter],
	);

	const getCurrentWeek = useCallback(
		() => createWeekFromDate(filters.date_debut),
		[filters.date_debut],
	);

	const getStatusColor = useCallback(
		(statut) => STATUS_COLORS[statut]?.background || STATUS_COLORS.default.background,
		[],
	);

	const getStatusTextColor = useCallback(
		(statut) => STATUS_COLORS[statut]?.text || STATUS_COLORS.default.text,
		[],
	);

	const updateFilters = useCallback((newFilters) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
	}, []);

	const isPastTime = useCallback((date, hour, minute) => {
		const now = new Date();
		const hourValue = parseInt(hour.split(':')[0]);
		const slotTime = new Date(date);
		slotTime.setHours(hourValue, minute, 0, 0);
		return slotTime < now;
	}, []);

	const isTimeSlotAvailable = useCallback(
		(carId, date, startTime, duration = 120) => {
			const [startHours, startMinutes] = startTime.split(':').map(Number);
			const slotStart = new Date(date);
			slotStart.setHours(startHours, startMinutes || 0, 0, 0);
			const slotEnd = new Date(slotStart);
			slotEnd.setMinutes(slotStart.getMinutes() + duration);

			const carInterventions = interventions.filter(
				(i) => i.car === carId && i.date_debut && i.date_fin,
			);

			return !carInterventions.some((intervention) => {
				const interventionStart = new Date(intervention.date_debut);
				const interventionEnd = new Date(intervention.date_fin);
				return slotStart < interventionEnd && slotEnd > interventionStart;
			});
		},
		[interventions],
	);

	const filterCarsByTechnician = useCallback((carsList, technicienId) => {
		if (!technicienId || technicienId === 'all' || !carsList?.length) {
			return carsList;
		}

		// Si c'est un ID de groupe (commence par "group_")
		if (technicienId.startsWith('group_')) {
			const groupId = technicienId.replace('group_', '');
			// Logique pour trouver la voiture associée à ce groupe
			// Cela dépend de comment vous structurez vos groupes
		}

		// Filtre par technicien individuel
		return carsList.filter((car) => {
			if (car.conducteurs && Array.isArray(car.conducteurs)) {
				return car.conducteurs.some(
					(conducteur) =>
						conducteur.technicien?._id === technicienId ||
						conducteur.technicien?.id === technicienId ||
						conducteur.technicien === technicienId,
				);
			}
			return false;
		});
	}, []);

	// =========== COMPUTED VALUES ===========
	const filteredCars = useMemo(() => {
		let filtered = cars;

		// Filtre par voiture
		if (filters.carFilter !== 'all') {
			filtered = filtered.filter((car) => car._id === filters.carFilter);
		}

		// Filtre par technicien (côté frontend)
		if (filters.technicienFilter !== 'all') {
			filtered = filterCarsByTechnician(filtered, filters.technicienFilter);
		}

		return filtered;
	}, [cars, filters.carFilter, filters.technicienFilter, filterCarsByTechnician]);

	// =========== EFFECTS ===========
	// Initial data load
	useEffect(() => {
		if (isInitialMount.current) {
			loadInitialData();
			isInitialMount.current = false;
		}
	}, [loadInitialData]);

	// Filter effects with debounce
	useEffect(() => {
		const filterTimer = setTimeout(() => {
			if (!isInitialMount.current) {
				// Create a stable filter object
				const currentFilters = {
					carFilter: filters.carFilter,
					technicienFilter: filters.technicienFilter,
					statutFilter: filters.statutFilter,
					reference: filters.reference,
					typeFilter: filters.typeFilter,
					date_debut: filters.date_debut,
					date_fin: filters.date_fin,
					viewMode: filters.viewMode,
				};

				// Check if we have valid date filters
				if (currentFilters.date_debut && currentFilters.date_fin) {
					console.log('usePlanningData: Loading interventions with filters');

					const loadPromises = [
						loadInterventionsData(currentFilters),
						loadCarsData(currentFilters),
					];

					Promise.all(loadPromises).catch((err) => {
						console.error('Error loading filtered data:', err);
					});
				}
			}
		}, DEBOUNCE_DELAY);

		return () => clearTimeout(filterTimer);
	}, [
		filters.carFilter,
		filters.technicienFilter,
		filters.statutFilter,
		filters.reference,
		filters.typeFilter,
		filters.date_debut?.toISOString(), // Use toISOString for stable dependency
		filters.date_fin?.toISOString(), // Use toISOString for stable dependency
		filters.viewMode,
	]);
	// =========== PAYMENT OPERATIONS ===========
	const addPaiement = useCallback(async (rapportId, paiementData, interventionData = null) => {
		return await serviceAddPaiement(
			rapportId,
			paiementData,
			interventionData,
			userDataRef.current,
		);
	}, []);

	const updatePaiement = useCallback(
		async (rapportId, paiementId, updates) => {
			return await serviceUpdatePaiement(
				rapportId,
				paiementId,
				updates,
				rapports,
				serviceUpdateGlobalPaiement,
			);
		},
		[rapports],
	);

	const deletePaiement = useCallback(
		async (rapportId, paiementId) => {
			return await serviceDeletePaiement(rapportId, paiementId, rapports);
		},
		[rapports],
	);

	const getPaiementsByRapport = useCallback(async (rapportId) => {
		return await serviceGetPaiementsByRapport(rapportId);
	}, []);

	const getPaiementStats = useCallback(async (rapportId) => {
		return await serviceGetPaiementStats(rapportId);
	}, []);

	const searchPaiements = useCallback(async (filters = {}) => {
		return await serviceSearchPaiements(filters);
	}, []);

	const createGlobalPaiement = useCallback(async (paiementData) => {
		return await serviceCreateGlobalPaiement(paiementData);
	}, []);

	const updateGlobalPaiement = useCallback(async (paiementId, paiementData) => {
		return await serviceUpdateGlobalPaiement(paiementId, paiementData);
	}, []);

	const syncPaiements = useCallback(
		async (rapportId, interventionData = null) => {
			return await serviceSyncPaiements(
				rapportId,
				interventionData,
				rapports,
				serviceCreateGlobalPaiement,
				userDataRef.current,
			);
		},
		[rapports],
	);

	const getGlobalPaiementDetails = useCallback(async (globalPaymentId) => {
		return await serviceGetGlobalPaiementDetails(globalPaymentId);
	}, []);

	const groupTechniciensByCar = useCallback(() => {
		const grouped = {};

		cars.forEach((car) => {
			const conducteurs = car.currentConducteurs || car.conducteurs || [];
			if (conducteurs.length > 0) {
				// Créer une clé unique pour le groupe (voiture + techniciens)
				const techIds = conducteurs
					.map((c) => c.technicien?.id || c.technicien)
					.filter(Boolean)
					.sort()
					.join('_');

				const carId = car._id;
				const key = `${carId}_${techIds}`;

				if (!grouped[key]) {
					const technicienNames = conducteurs
						.map((c) => c.technicien?.userName || c.technicien?.name || 'Inconnu')
						.join(' & ');

					// Compter les interventions du jour pour ce groupe
					const today = new Date();
					today.setHours(0, 0, 0, 0);

					const interventionsToday = interventions.filter((intervention) => {
						if (!intervention.date_debut || !intervention.cars) return false;

						// Vérifier si l'intervention est pour cette voiture
						const hasCar = Array.isArray(intervention.cars)
							? intervention.cars.some((c) => c._id === carId)
							: intervention.cars === carId;

						if (!hasCar) return false;

						// Vérifier si c'est aujourd'hui
						const interventionDate = new Date(intervention.date_debut);
						return (
							interventionDate.toDateString() === today.toDateString() &&
							(intervention.statut === 'planifie' ||
								intervention.statut === 'en_cours')
						);
					}).length;

					grouped[key] = {
						id: key, // Utiliser la clé comme ID unique
						carId: carId,
						carInfo: {
							matricule: car.matricule,
							marque: car.marque,
							modele: car.modele,
						},
						techniciens: conducteurs.map((c) => ({
							id: c.technicien?.id || c.technicien,
							name: c.technicien?.userName || c.technicien?.name || 'Inconnu',
						})),
						displayName: `${technicienNames} (${car.matricule})`,
						interventionsToday: interventionsToday,
					};
				}
			}
		});

		return Object.values(grouped);
	}, [cars, interventions]);

	const getGroupedTechniciens = useCallback(() => {
		return groupTechniciensByCar();
	}, [groupTechniciensByCar]);

	// =========== RETURN OBJECT ===========
	return {
		// =========== DONNÉES ===========
		cars: filteredCars,
		interventions,
		clients,
		techniciens,
		chronologies,
		rapports,
		loading,
		filters,
		error,

		// =========== CONSTANTES ===========
		CHRONOLOGIE_TYPES,
		INTERVENTION_TYPES,
		INTERVENTION_MOTIFS,
		INTERVENTION_STATUTS,
		CAR_STATUTS,

		// =========== CRUD INTERVENTIONS ===========
		addIntervention,
		updateIntervention,
		updateInterventionState,
		deleteIntervention,
		transferInterventions,
		getInterventionById,
		refreshIntervention,

		// =========== CRUD CHRONOLOGIE ===========
		addChronologieEntry,
		getChronologieByIntervention,
		loadChronologieByIntervention,

		// =========== CRUD RAPPORTS ===========
		createRapport,
		updateRapport,
		deleteRapport,
		getRapportByIntervention,
		loadRapportByIntervention,

		// =========== FONCTIONS DE PAIEMENT ===========
		addPaiement,
		updatePaiement,
		deletePaiement,
		getPaiementsByRapport,
		getPaiementStats,
		searchPaiements,
		createGlobalPaiement,
		updateGlobalPaiement,
		syncPaiements,
		getGlobalPaiementDetails,

		// =========== UTILITAIRES ===========
		getInterventionsByCarAndDate,
		getCurrentWeek,
		getStatusColor,
		getStatusTextColor,
		isPastTime,
		isTimeSlotAvailable,
		updateFilters,

		// =========== CHARGEMENT DE DONNÉES ===========
		loadCars: loadCarsData,
		loadInterventions: loadInterventionsData,
		loadClients: loadClientsData,
		loadTechniciens: loadTechniciensData,
		reloadData: loadAllData,

		getGroupedTechniciens,
		groupTechniciensByCar,
	};
};
